import { Link } from "react-router-dom";
import { Article } from "@/mocks/data";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors"
      >
        <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.thumbnailUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex flex-col justify-between flex-1 min-w-0">
          <div>
            <h3 className="text-foreground-950 font-semibold text-base group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-600 line-clamp-2">
              {article.excerpt}
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-500 mt-3 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-3 py-3 border-b border-background-200 last:border-b-0"
      >
        <div className="flex-1 min-w-0">
          <h4 className="text-sm text-foreground-950 font-medium group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">
            {article.publishDate} · {article.readTime}
          </p>
        </div>
        <div className="w-20 h-14 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.thumbnailUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.thumbnailUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-4">
        <h3 className="text-foreground-950 font-semibold text-base group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {article.publishDate}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}